// NexaStaff Quality of Life Analytics Integration
// 🔥 FIRE AND FORGET - Never crashes main site

class NexaQOLAnalytics {
  constructor() {
    try {
      this.endpoint = '/api/qol';
      this.sessionId = this.getSessionId();
      this.maxScroll = 0;
      this.startTime = Date.now();
      this.enabled = true;
      this.init();
    } catch (error) {
      // If constructor fails, disable completely
      this.enabled = false;
    }
  }

  init() {
    try {
      // Only initialize if we're in the browser and enabled
      if (typeof window === 'undefined' || !this.enabled) return;
      
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.safeSetupTracking());
      } else {
        this.safeSetupTracking();
      }
    } catch (error) {
      this.enabled = false;
    }
  }

  safeSetupTracking() {
    try {
      this.setupTracking();
    } catch (error) {
      this.enabled = false;
    }
  }

  getSessionId() {
    try {
      if (typeof window === 'undefined') return 'ssr';
      
      let sessionId = sessionStorage.getItem('nexa_qol_session');
      if (!sessionId) {
        sessionId = crypto.randomUUID();
        sessionStorage.setItem('nexa_qol_session', sessionId);
      }
      return sessionId;
    } catch (error) {
      // If sessionStorage fails, generate random ID
      return 'temp_' + Math.random().toString(36).substr(2, 9);
    }
  }

  async track(type, data = {}) {
    // NEVER throw errors - always fail silently
    if (!this.enabled || typeof window === 'undefined') return;
    
    try {
      const payload = {
        type,
        url: window.location.href,
        title: document.title,
        referrer: document.referrer,
        time: Date.now(),
        session: this.sessionId,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        path: window.location.pathname,
        ...data
      };

      // Use Promise.resolve to make it truly fire-and-forget
      Promise.resolve().then(async () => {
        try {
          await fetch(this.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        } catch (networkError) {
          // Network errors are completely ignored
        }
      });
      
    } catch (error) {
      // Any tracking error is completely ignored
    }
  }

  setupTracking() {
    if (!this.enabled) return;

    try {
      // Track initial pageview
      this.trackPageview();
    } catch (error) {
      // Ignore pageview tracking errors
    }

    try {
      // Track clicks for heatmaps  
      document.addEventListener('click', (event) => {
        try {
          this.handleClick(event);
        } catch (error) {
          // Ignore click tracking errors
        }
      });
    } catch (error) {
      // Ignore click listener setup errors
    }

    try {
      // Track scroll depth
      window.addEventListener('scroll', () => {
        try {
          this.handleScroll();
        } catch (error) {
          // Ignore scroll tracking errors
        }
      });
    } catch (error) {
      // Ignore scroll listener setup errors
    }

    try {
      // Track page exits
      window.addEventListener('beforeunload', () => {
        try {
          this.handleExit();
        } catch (error) {
          // Ignore exit tracking errors
        }
      });
    } catch (error) {
      // Ignore exit listener setup errors
    }

    try {
      // Track React route changes (for SPAs)
      this.trackRouteChanges();
    } catch (error) {
      // Ignore route tracking errors
    }
  }

  trackPageview() {
    if (!this.enabled) return;
    this.track('view', {
      path: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash
    });
  }

  handleClick(event) {
    if (!this.enabled) return;
    try {
      const element = event.target;
      
      this.track('click', {
        element: element.tagName,
        class: element.className,
        id: element.id,
        text: element.textContent?.slice(0, 50) || '',
        href: element.href || '',
        x: event.clientX,
        y: event.clientY
      });
    } catch (error) {
      // Ignore click handling errors
    }
  }

  handleScroll() {
    if (!this.enabled) return;
    try {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        try {
          const scrollPercent = Math.round(
            (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
          );
          
          if (scrollPercent > this.maxScroll && scrollPercent > 0) {
            this.maxScroll = scrollPercent;
            this.track('scroll', { 
              depth: scrollPercent
            });
          }
        } catch (error) {
          // Ignore scroll calculation errors
        }
      }, 150);
    } catch (error) {
      // Ignore scroll timer errors
    }
  }

  handleExit() {
    if (!this.enabled) return;
    try {
      const timeOnPage = Math.round((Date.now() - this.startTime) / 1000);
      
      const payload = JSON.stringify({
        type: 'exit',
        url: window.location.href,
        session: this.sessionId,
        time: Date.now(),
        duration: timeOnPage,
        max_scroll: this.maxScroll,
        path: window.location.pathname
      });
      
      // Use sendBeacon which is fire-and-forget by design
      if (navigator.sendBeacon) {
        navigator.sendBeacon(this.endpoint, payload);
      }
    } catch (error) {
      // Ignore exit tracking errors
    }
  }

  // Track React Router changes
  trackRouteChanges() {
    if (!this.enabled) return;
    try {
      let currentPath = window.location.pathname;
      
      // Check for route changes every 100ms (for React Router)
      setInterval(() => {
        try {
          if (window.location.pathname !== currentPath) {
            currentPath = window.location.pathname;
            this.maxScroll = 0; // Reset scroll tracking for new page
            this.startTime = Date.now(); // Reset timer for new page
            setTimeout(() => {
              try {
                this.trackPageview();
              } catch (error) {
                // Ignore pageview tracking errors
              }
            }, 100);
          }
        } catch (error) {
          // Ignore route change detection errors
        }
      }, 100);
    } catch (error) {
      // Ignore route tracking setup errors
    }
  }

  // 🔥 FIRE AND FORGET Public API
  trackEvent(name, properties = {}) {
    if (!this.enabled) return;
    try {
      this.track('event', { name, properties });
    } catch (error) {
      // Completely ignore all errors
    }
  }

  trackQOLIssue(issue, details = {}) {
    if (!this.enabled) return;
    try {
      this.track('qol_issue', { name: issue, properties: details });
    } catch (error) {
      // Completely ignore all errors
    }
  }

  trackPerformance(metric, value) {
    if (!this.enabled) return;
    try {
      this.track('performance', { name: metric, value: value });
    } catch (error) {
      // Completely ignore all errors
    }
  }
}

// 🔥 FIRE AND FORGET initialization
let qolAnalytics;
try {
  qolAnalytics = new NexaQOLAnalytics();
} catch (error) {
  // If initialization completely fails, create dummy object
  qolAnalytics = {
    trackEvent: () => {},
    trackQOLIssue: () => {},
    trackPerformance: () => {},
    enabled: false
  };
  // debug suppressed
}

// Export for manual usage
export default qolAnalytics;

// Make available globally with safe fallbacks
if (typeof window !== 'undefined') {
  window.nexaQOL = qolAnalytics;
} 